import { useState } from "react";
import { Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface YouTubeFacadeProps {
    videoId: string;
    title: string;
    className?: string;
    /** Extra params appended to the embed URL once the user clicks play (e.g. loop/mute) */
    embedParams?: string;
    playButtonSize?: "sm" | "lg";
    /**
     * Set only for a genuinely above-the-fold thumbnail (currently just the
     * hero). It makes the image eager with fetchpriority="high" so it can be
     * the LCP element.
     *
     * Off by default on purpose: this component also renders galleries of
     * thumbnails further down the pediatric and promotional pages, and marking
     * every one of those high-priority would have them compete for bandwidth
     * with the actual LCP image — making the metric worse, not better.
     */
    priority?: boolean;
}

/**
 * Lightweight "facade" for a YouTube embed: renders only a static thumbnail + play
 * button until clicked, so the heavy iframe (and its JS) never loads on first paint.
 */
const YouTubeFacade = ({ videoId, title, className = "", embedParams = "", playButtonSize = "lg", priority = false }: YouTubeFacadeProps) => {
    const { t } = useLanguage();
    const [loaded, setLoaded] = useState(false);

    if (loaded) {
        return (
            <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1${embedParams}`}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className={`w-full h-full border-0 ${className}`}
            />
        );
    }

    return (
        <button
            type="button"
            onClick={() => setLoaded(true)}
            aria-label={`${t('playVideoLabel')}: ${title}`}
            className={`group relative w-full h-full overflow-hidden ${className}`}
        >
            <img
                src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                alt={title}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
                width={480}
                height={360}
                decoding="async"
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
            <div
                className={
                    playButtonSize === "lg"
                        ? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform"
                        : "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform"
                }
            >
                <Play className={playButtonSize === "lg" ? "w-6 h-6 text-white fill-white ml-1" : "w-5 h-5 text-white fill-white ml-0.5"} />
            </div>
        </button>
    );
};

export default YouTubeFacade;
